import { NgClass, NgFor, NgIf } from '@angular/common';
import { AfterViewInit, Component, Input } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { environment } from '@environments/environment';
import { GoogleAutocompleteAddress, NgxAddressAutocompleteComponent } from '@medology/ngx-address-autocomplete';
import { Address } from '@models/address';

@Component({
  selector: 'app-address-inputs',
  templateUrl: './address-inputs.component.html',
  styleUrls: ['./address-inputs.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, NgClass, NgIf, NgFor, NgxAddressAutocompleteComponent],
})
export class AddressInputsComponent implements AfterViewInit {
  @Input() public form: FormGroup;
  @Input() public multiregion: boolean = true;
  public Address: typeof Address = Address;

  /**
   * Gets the Google Maps API key from the config.
   *
   * @return {string}
   */
  public get googleApiKey(): string {
    return environment.googleMapApiKey;
  }

  /**
   * Gets the street address control from the form.
   *
   * @return {FormControl} the street address control
   */
  public get streetAddressControl() {
    return <FormControl>this.form.get('streetAddress');
  }

  /**
   * Gets the input type for the zip code field according to the selected state.
   *
   * @return {'tel' | 'text'} the input type for the zip code field
   */
  public get zipCodeInputType(): 'tel' | 'text' {
    if (this.stateBelongsToCountry(this.selectedState, 'US')) {
      return 'tel';
    }

    return 'text';
  }

  /**
   * Gets the placeholder for the zip code field according to the selected state.
   *
   * @return {'Zip Code' | 'Postal Code'} the placeholder for the zip code field
   */
  public get zipCodePlaceholder(): 'Zip Code' | 'Postal Code' {
    if (this.stateBelongsToCountry(this.selectedState, 'CA')) {
      return 'Postal Code';
    }

    return 'Zip Code';
  }

  /**
   * Determines if the street address 2 field should be displayed.
   */
  public get shouldDisplayStreetAddress2(): boolean {
    return this.form?.contains('streetAddress2');
  }

  /**
   * Gets the selected state from the form.
   *
   * @return {string} the selected state
   */
  private get selectedState(): string {
    return this.form?.get('state')?.value;
  }

  /**
   * After view init lifecycle hook.
   */
  public ngAfterViewInit(): void {
    if (!this.multiregion) {
      this.setUSAddressZipCodeValidators();
    }
  }

  /**
   * Determines if a form field is invalid.
   *
   * @param field string The name of the field to check vor validity.
   */
  public isInvalid(field: string = null): boolean {
    let input = field ? this.form?.get(field) : this.form;

    return input?.invalid && input.touched;
  }

  /**
   * Determines if a form field is failing a specific validation.
   *
   * @param errorName name of the validation that could be giving error
   * @param field the name of the field to check for specific validation error
   *
   * @returns true if the field has the specific error, false otherwise
   */
  public hasSpecificError(errorName: string, field: string): boolean {
    const input = this.form.get(field);

    return !!input?.errors?.[errorName];
  }

  /**
   * Handles the place changed event.
   */
  protected selectLocation(address: GoogleAutocompleteAddress): void {
    try {
      this.fillAddress(this.getAddressFromSelectedPlace(address));
    } catch (e) {
      const streetAddressControl = this.form.get('streetAddress');
      streetAddressControl.setValue('');
      console.warn(e);
    }
  }

  /**
   * Fills the address form out with the given address.
   *
   * @param address Address The address to fill the form with.
   */
  private fillAddress(address: Address): void {
    for (let prop in address) {
      if (Object.prototype.hasOwnProperty.call(address, prop)) {
        this.form.get(prop)?.setValue(address[prop]);
      }
    }

    this.form.markAllAsTouched();
  }

  /**
   * Gets the address from the selected place.
   */
  private getAddressFromSelectedPlace(address: GoogleAutocompleteAddress): Address {
    return new Address({
      streetAddress: address.address,
      streetAddress2: address.address2,
      city: address.city,
      state: this.isValidState(address.state) ? address.state : null,
      zipcode: address.zipcode,
    });
  }

  /**
   * Checks if a given state belongs to a specific country.
   *
   * @param {string} state the abbreviation of the state to check
   * @param {'US' | 'CA'} country the country code ('US' for United States, 'CA' for Canada)
   *
   * @returns {boolean} true if the state belongs to the specified country, otherwise false
   */
  private stateBelongsToCountry(state: string, country: 'US' | 'CA'): boolean {
    return Address[`states${country}`].some((countryState) => countryState.abbv === state);
  }

  /**
   * Alter the validators for the address zip code field to only US.
   */
  private setUSAddressZipCodeValidators(): void {
    this.form?.get('zipcode').clearValidators();
    this.form?.get('zipcode').setValidators([Validators.required, Validators.pattern('^[0-9]{5}$')]);
    this.form?.get('zipcode').updateValueAndValidity();
  }

  /**
   * Verifies if the provided state abbreviation is valid within the allowed regions.
   *
   * @param {string} state the abbreviation of the state to validate
   *
   * @returns {boolean} true if the state abbreviation is valid, otherwise false
   */
  private isValidState(state: string): boolean {
    const usStates = Address.statesUS.map((state) => state.abbv);
    const caStates = Address.statesCA.map((state) => state.abbv);

    return this.multiregion ? usStates.includes(state) || caStates.includes(state) : usStates.includes(state);
  }
}
